import type { AuthService } from "@storytell/auth";

/**
 * Auth Gating Configuration
 *
 * Centralized configuration for which features require authentication.
 * Set to `true` to require authentication for the feature, `false` to allow guest access.
 */
const authGatingConfig = {
  // Core actions
  submitPrompt: true,
  uploadAssets: true,

  // Project management
  createProject: true,
  manageProject: true,
  shareProject: true,

  // Account / settings — everything outside /settings/general is gated.
  manageDevices: true,
  manageTokens: true,
  manageBilling: true,
  manageMemory: true,
} as const;

export type AuthGatingConfig = typeof authGatingConfig;

export type AuthGatingFeature = keyof AuthGatingConfig;

/**
 * Decide whether a gated action may run for the current viewer.
 *
 * - `allow` — the feature is not gated, or the user is registered: run the action.
 * - `pending` — auth has not resolved yet: run nothing and show nothing.
 * - `gated` — the user must register: open the auth gate modal.
 *
 * `isRegistered()` is false while auth is still connecting and while it is in its error state, so
 * a caller that reads it without first waiting on `isReady()` tells signed-in users to sign up.
 * That readiness wait is part of the decision, not something each call site adds for itself.
 *
 * @param auth - The auth service (`wire.auth`)
 * @param feature - Feature key from authGatingConfig, or `null` to always require registration
 */
export const authGateStatus = (
  auth: Pick<AuthService, "isReady" | "isRegistered">,
  feature: AuthGatingFeature | null
): "allow" | "pending" | "gated" => {
  const gated: boolean = feature === null || authGatingConfig[feature];
  if (!gated) return "allow";
  if (!auth.isReady()) return "pending";
  return auth.isRegistered() ? "allow" : "gated";
};

/**
 * Where a signed-out visitor who opens a page only an account can use is sent: the sign-in form,
 * since a browser that followed a link to an account page most likely belongs to someone who has
 * one. `undefined` means stay — auth is still connecting, or the viewer is not a guest.
 *
 * isGuest() answers from a loading lifecycle rather than a resolved session until isReady()
 * finishes, so it must be read after that check — same ordering constraint as isRegistered() above.
 */
export const guestSignInHref = (auth: Pick<AuthService, "isReady" | "isGuest">): string | undefined =>
  auth.isReady() && auth.isGuest() ? "/auth/signin" : undefined;
